import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { Button, Link, styled, Typography } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { Formik, Form } from 'formik';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { DO_LOGIN_URL, RETURN_URL_QUERY_PARAM_KEY, VALIDATE_LOGIN_URL } from '../../../constants';
import { useConfigurationContext } from '../../../contexts/ConfigurationContext';
import { useUserCredentialsContext } from '../../../contexts/UserCredentialsContext';
import { getCustomAxios, handleAxiosError } from '../../../helpers';
import { useLongPress } from '../../../hooks';
import { AccountPanes } from '../../../models';
import { useLoginConfigurationQuery } from '../../../models/api/useLoginConfigurationQuery';
import { LocalizationKeys } from '../../../models/localizationKeys';
import { Input } from '../Input';
import { PasswordInput } from '../PasswordInput';
import { Register } from '../Register';
import { SocialMedia } from '../SocialMedia';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var LoginRoot = styled('div')(function (_ref) {
  var theme = _ref.theme;
  return {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: theme.spacing(3, 4)
  };
});
var LoginHeaderLabel = styled(Typography)(function (_ref2) {
  var theme = _ref2.theme;
  return {
    marginBottom: theme.spacing(2),
    textAlign: 'center'
  };
});
var LoginForm = styled(Form)(function () {
  return {
    display: 'flex',
    flexDirection: 'column'
  };
});
var LoginFormError = styled(Form)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    color: theme.palette.error.main,
    margin: theme.spacing(0, 2)
  };
});
var LoginFormSubmit = styled(Button)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    height: 40,
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 6),
    userSelect: 'none'
  };
});
var LoginForgotPassword = styled(Link)(function (_ref5) {
  var theme = _ref5.theme;
  return {
    cursor: 'pointer',
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(2, 'auto')
  };
});
export var Login = function Login(_ref6) {
  var goToPane = _ref6.goToPane;
  var formRef = useRef(null);

  var _useUserCredentialsCo = useUserCredentialsContext(),
      setCustomerID = _useUserCredentialsCo.setCustomerID,
      setPassword = _useUserCredentialsCo.setPassword;

  var _useState = useState(undefined),
      _useState2 = _slicedToArray(_useState, 2),
      errorMessage = _useState2[0],
      setErrorMessage = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isSubmitLocked = _useState4[0],
      setIsSubmitLocked = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      returnUrl = _useState6[0],
      setReturnUrl = _useState6[1];

  var _useTranslation = useTranslation(),
      _useTranslation2 = _slicedToArray(_useTranslation, 1),
      translate = _useTranslation2[0];

  var _useTestId = useTestId(),
      getTestId = _useTestId.getTestId;

  var handleForgotPasswordLinkClicked = useCallback(function () {
    return goToPane(AccountPanes.ForgotPassPane);
  }, [goToPane]);
  var loginSchema = Yup.object().shape({
    Password: Yup.string().required(translate(LocalizationKeys.enterPassword.id)),
    Username: Yup.string().required(translate(LocalizationKeys.enterEmailOrBrandPartnerNumber.id))
  });

  var _useConfigurationCont = useConfigurationContext(),
      ssoAdLoginUrl = _useConfigurationCont.ssoAdLoginUrl,
      currentLanguage = _useConfigurationCont.currentLanguage,
      onlineBaseUrl = _useConfigurationCont.onlineBaseUrl;

  var validateLoginUrl = new URL(VALIDATE_LOGIN_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);
  var doLoginUrl = new URL(DO_LOGIN_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);

  var onAdLoginSubmit = function onAdLoginSubmit() {
    if (ssoAdLoginUrl) {
      window.location.href = ssoAdLoginUrl;
    }
  };

  var longPress = useLongPress(onAdLoginSubmit);

  var passwordNeedChange = function passwordNeedChange(values) {
    var PasswordChangeRequired = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

    if (PasswordChangeRequired) {
      setCustomerID(values.Username);
      setPassword(values.Password);
      goToPane(AccountPanes.NewPassSuperUserPane);
    }
  };

  var loginConfiguration = useLoginConfigurationQuery();

  var onLoginSubmit = function onLoginSubmit(errorMessageSetCallback) {
    return async function (values, actions) {
      actions.setSubmitting(false);

      if (!isSubmitLocked) {
        setIsSubmitLocked(true);
        await getCustomAxios(currentLanguage).post(validateLoginUrl.toString(), values).then(function (_ref7) {
          var _ref7$data = _ref7.data,
              Success = _ref7$data.Success,
              PasswordChangeRequired = _ref7$data.PasswordChangeRequired,
              Message = _ref7$data.Message;

          if (Success) {
            try {
              if (!formRef.current) {
                errorMessageSetCallback(translate(LocalizationKeys.failedToSubmit.id));
                return;
              }

              formRef.current.submit();
            } catch (_unused) {
              errorMessageSetCallback(translate(LocalizationKeys.failedToSubmit.id));
            }
          } else {
            errorMessageSetCallback(Message);
          }

          passwordNeedChange(values, PasswordChangeRequired);
        }).catch(function (error) {
          handleAxiosError({
            error: error,
            translateMessage: function translateMessage(key) {
              return errorMessageSetCallback(translate(LocalizationKeys[key].id));
            }
          });
        }).finally(function () {
          return setIsSubmitLocked(false);
        });
      }
    };
  };

  useEffect(function () {
    var _URL = new URL(window.location.href),
        searchParams = _URL.searchParams;

    var url = searchParams.get(RETURN_URL_QUERY_PARAM_KEY);

    if (!url) {
      return;
    }

    if (window.location.search.includes('support-center')) {
      window.history.pushState(null, 'support-center', "".concat(RETURN_URL_QUERY_PARAM_KEY, "=%2Fsupport-center"));
    }

    setReturnUrl(url);
  }, []);
  return /*#__PURE__*/_jsxs(LoginRoot, {
    "data-testid": getTestId('login-wrapper'),
    children: [/*#__PURE__*/_jsx(LoginHeaderLabel, {
      variant: "h6",
      "data-testid": getTestId('login-title'),
      children: translate(LocalizationKeys.logIn.id)
    }), /*#__PURE__*/_jsx(Formik, {
      initialValues: {
        Username: '',
        Password: ''
      },
      validationSchema: loginSchema,
      onSubmit: onLoginSubmit(setErrorMessage),
      children: /*#__PURE__*/_jsxs(LoginForm, {
        ref: formRef,
        method: "POST",
        action: doLoginUrl.toString(),
        "data-testid": getTestId('login-form'),
        children: [errorMessage && /*#__PURE__*/_jsx(LoginFormError, {
          "data-testid": getTestId('login-error-msg'),
          children: errorMessage
        }), /*#__PURE__*/_jsx(Input, {
          autoComplete: "username",
          type: "text",
          name: "Username",
          label: "".concat(translate(LocalizationKeys.emailAddressOrRegistrationNumber.id), "*")
        }), /*#__PURE__*/_jsx(PasswordInput, {
          autoComplete: "current-password",
          name: "Password"
        }), /*#__PURE__*/_jsx("input", {
          hidden: true,
          readOnly: true,
          name: "ReturnUrl",
          value: returnUrl
        }), /*#__PURE__*/_jsx(LoginFormSubmit, _objectSpread(_objectSpread({
          disableRipple: true,
          disabled: isSubmitLocked,
          color: "primary",
          variant: "contained",
          type: "submit",
          "data-testid": getTestId('login-submit')
        }, longPress), {}, {
          children: translate(LocalizationKeys.logIn.id)
        }))]
      })
    }), /*#__PURE__*/_jsx(LoginForgotPassword, {
      type: "button",
      "data-testid": getTestId('forgot-password'),
      onClick: handleForgotPasswordLinkClicked,
      children: translate(LocalizationKeys.forgotPassword.id)
    }), /*#__PURE__*/_jsx(SocialMedia, {}), (loginConfiguration === null || loginConfiguration === void 0 ? void 0 : loginConfiguration.ShowRegistrationLink) && /*#__PURE__*/_jsx(Register, {
      loginConfiguration: loginConfiguration
    })]
  });
};
export default Login;